import { useState, useEffect, useMemo } from 'react';
import api from '../../../api';
import { ActivityLogger } from '../../../utils/activityLogger';
import { validateLeadForm } from '../../../utils/validation';
import { updateFormData } from '../../../utils/formHandlers';
import { sendLeadEmailToUser, getLeadEmailTemplates, checkEmailSettings } from '../api/leadEmailApi';

const isValidUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(str));

export const useLeadsForm = () => {
  const initialFormData = {
    company: '',
    contact: '',
    contactPhoneNumber: '',
    email: '',
    address: '',
    companyPhoneNumber: '',
    sector: '',
    companyWebsite: '',
    stage: '',
    projectName: '',
    projectDescription: '',
    leadSource: '',
    services: [],
    standards: [],
    assignedUser: '',
    competitor: [],
    departmentId: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [formErrors, setFormErrors] = useState({});
  const [serviceSections, setServiceSections] = useState([0]);
  // HAPUS: const [uploadedFiles, setUploadedFiles] = useState({...});

  const [options, setOptions] = useState({
    companyOptions: [],
    contactOptions: [],
    leadSourceOptions: [],
    assigneeOptions: [],
    stageOptions: [],
    sectorOptions: [],
    servicesOptions: [],
    standardsOptions: [],
    assignedUserOptions: [],
    serviceOptions: [],
    competitorOptions: [],
    departmentOptions: []
  });

  // Raw user list with departmentId for cascade filtering
  const [allAssignedUsers, setAllAssignedUsers] = useState([]);

  // New: HRIS user options for form Assigned To cascade (separate from existing assignedUserOptions)
  const [formCascadeUsers, setFormCascadeUsers] = useState([]);
  const [formCascadeDepts, setFormCascadeDepts] = useState([]);

  const [standardsOptionsMap, setStandardsOptionsMap] = useState({});

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchInitialOptions();
    
  }, []);

  const fetchCompetitorOptions = async () => {
    try {
      const res = await api.get('/crm/lead-competitors');
      const formatted = (res.data || []).map(c => ({
        value: String(c.id ?? c.value ?? ''),
        label: c.name ?? c.label ?? '',
        ...c
      }));
      setOptions(prev => ({ ...prev, competitorOptions: formatted }));
    } catch (error) {
      setOptions(prev => ({ ...prev, competitorOptions: [] }));
    }
  };

  const fetchInitialOptions = async () => {
    try {
      await Promise.all([
        fetchCompanyOptions(),
        fetchLeadSourceOptions(),
        fetchServicesOptions(),
        fetchUsersAndDepts(),
        fetchStageOptions(),
        fetchCompetitorOptions(),
        fetchDepartmentOptions(),
      ]);
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchCompanyOptions = async () => {
    try {
      const res = await api.get('/v2/crm/companies/options');
      // CompanyControllerV2#getCompanyOptions returns a bare List, not
      // { items: [...] }. Reading res.data.items threw a TypeError that the
      // catch below swallowed, leaving companyOptions permanently empty —
      // which is what kept the read-only Sector field blank. Accept both shapes.
      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.items)
          ? res.data.items
          : [];
      const formatted = raw.map(c => ({
        value: c.value,
        label: c.label,
        name: c.name,
        website: c.website,
        contactNumber: c.contactNumber,
        address: c.address,
        sector: c.sector,
        sectorName: c.sectorName
      }));
      setOptions(prev => ({ ...prev, companyOptions: formatted }));
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchLeadSourceOptions = async () => {
    try {
      const res = await api.get('/crm/sources/options');
      const formatted = res.data.map(src => ({
        value: src.id,
        label: src.name,
        ...src
      }));
      setOptions(prev => ({ ...prev, leadSourceOptions: formatted }));
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchServicesOptions = async () => {
    try {
      const res = await api.get('/crm/services/options');
      setOptions(prev => ({ ...prev, servicesOptions: res.data }));
    } catch (err) {
      // Handle error silently
    }
  };

  // Single fetch for all user + department data — avoids calling /option-internal-user twice
  const fetchUsersAndDepts = async () => {
    try {
      const [usersRes, deptsRes] = await Promise.all([
        api.get('/md/internal-users/option-internal-user'),
        api.get('/md/departement')
      ]);

      const usersData = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.data || [];
      const deptsData = Array.isArray(deptsRes.data) ? deptsRes.data : deptsRes.data?.data || [];

      // Build departmentId lookup from /departement response (avoids the extra /md/internal-users full-list call)
      const deptMap = new Map();
      usersData.forEach(u => {
        const deptId = u.departmentId || u.departementId;
        if (u.id != null) deptMap.set(String(u.id ?? u.value ?? ''), deptId != null ? String(deptId) : null);
      });

      const assignedUserOptions = usersData.map(u => ({
        value: u.value ?? u.id,
        label: u.label || u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        employeeId: u.employeeId,
        fullName: u.fullName,
        departmentId: deptMap.get(String(u.value ?? u.id ?? '')) || null
      }));
      setAllAssignedUsers(assignedUserOptions);
      setOptions(prev => ({ ...prev, assignedUserOptions }));

      // Cascade users for the form dropdown (same data, different shape)
      const cascadeUsers = usersData.map(u => {
        const firstName = u.firstName || '';
        const lastName = u.lastName || '';
        const fullName = (firstName + ' ' + lastName).trim() || u.label || u.fullName || '';
        return {
          value: String(u.id ?? u.value ?? ''),
          label: fullName,
          email: u.email,
          departmentId: String(u.departmentId || u.departementId || ''),
          status: String(u.status ?? '1')
        };
      }).filter(u => u.value && u.label.trim() !== '');
      setFormCascadeUsers(cascadeUsers);

      setFormCascadeDepts(deptsData.map(d => ({
        value: String(d.id || ''),
        label: d.name || ''
      })).filter(d => d.value && d.label));
    } catch (_) {
      // non-fatal
    }
  };

  const fetchDepartmentOptions = async () => {
    try {
      const res = await api.get('/crm/departments');
      const departmentData = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      const formatted = departmentData.map(dept => ({
        value: dept.value || dept.id,
        label: dept.label || dept.name,
        ...dept
      }));
      setOptions(prev => ({ ...prev, departmentOptions: formatted }));
    } catch (error) {
      setOptions(prev => ({ ...prev, departmentOptions: [] }));
    }
  };

  const fetchStageOptions = async () => {
    try {
      const res = await api.get('/crm/lead-statuses/options');
      const formatted = res.data.map(item => ({
        value: item.value,
        label: item.label
      }));
      setOptions(prev => ({ ...prev, stageOptions: formatted }));
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchContactOptions = async (companyId) => {
    if (!companyId) return;
    try {
      const res = await api.get(`/crm/contacts/by-company/${companyId}`);
      const formatted = res.data.map(c => ({
        value: c.id,
        label: c.name,
        companyId: c.companyId,
        ...c
      }));
      setOptions(prev => ({ ...prev, contactOptions: formatted }));
    } catch (error) {
      // Handle error silently
    }
  };

  const fetchStandardsByServiceId = async (index, serviceId) => {
    if (!serviceId) return;
    try {
      const res = await api.get(`/crm/standards/by-service/${serviceId}`);
      setStandardsOptionsMap(prev => ({
        ...prev,
        [index]: res.data
      }));
    } catch (err) {
      // Handle error silently
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => updateFormData(prev, name, value));

    if (name === 'company') {
      const selected = options.companyOptions.find(c => String(c.value) === String(value));
      if (selected) {
        setFormData(prev => ({
          ...prev,
          company: value,
          sector: selected.sector || '',
          companyWebsite: selected.website || '',
          companyPhoneNumber: selected.contactNumber || '',
          address: selected.address || ''
        }));
      }
      // ✅ FIX: Always fetch contacts when company is set, even if not found in options
      // This handles cases where company is set programmatically (e.g., from Clients page)
      if (value && value !== '') {
        fetchContactOptions(value);
      }
    }

    const match = name.match(/^services-(\d+)$/);
    if (match) {
      const index = parseInt(match[1]);
      fetchStandardsByServiceId(index, value);
    }
  };

  const handleTextChange = (e) => {
    const { name, value } = e.target;
    handleInputChange(name, value);
  };

  const validateForm = () => {
    const errors = validateLeadForm(formData, serviceSections);
    setFormErrors(errors);
    return errors;
  };

  // Check if a service is ISCC
  const isIsccService = (serviceId) => {
    const service = options.servicesOptions?.find(opt => opt.value === serviceId);
    return service?.label?.toLowerCase().includes('iscc') || 
           service?.code?.toLowerCase().includes('iscc');
  };

  const resolveCompetitorIds = async (competitors) => {
    const ids = [];
    for (const c of competitors.filter(Boolean)) {
      const val = typeof c === 'object' && c?.value != null ? c.value : String(c);
      if (isValidUUID(val)) {
        ids.push(val);
      } else if (val && typeof val === 'string') {
        try {
          const created = await api.post('/crm/lead-competitors', { name: val.trim() });
          if (created?.data?.id) ids.push(String(created.data.id));
        } catch (err) {
          if (err?.response?.status === 409) {
            const existing = await api.get(`/crm/lead-competitors/search?name=${encodeURIComponent(val.trim())}`);
            const match = (existing?.data || []).find((x) => (x.name || '').toLowerCase() === val.trim().toLowerCase());
            if (match?.id) ids.push(String(match.id));
          } else throw err;
        }
      }
    }
    return [...new Set(ids)];
  };

  const createLeadPayload = async (formData, serviceSections) => {
    const competitors = Array.isArray(formData.competitor) ? formData.competitor : (formData.competitor ? [formData.competitor] : []);
    const leadCompetitorIds = await resolveCompetitorIds(competitors);
    return {
      companyId: formData.company || null,
      contactId: formData.contact || null,
      assignedUserId: formData.assignedUser || null,
      contactPhoneNumber: formData.contactPhoneNumber,
      email: formData.email,
      address: formData.address,
      companyPhoneNumber: formData.companyPhoneNumber,
      companyWebsite: formData.companyWebsite,
      projectName: formData.projectName,
      projectDescription: formData.projectDescription,
      leadSource: formData.leadSource || null,
      stage: formData.stage || null,
      departmentId: formData.departmentId || null,
      leadCompetitorIds,
      services: serviceSections.map(index => {
        const serviceId = formData[`services-${index}`];
        const standardIds = (formData[`standards-${index}`] || []).filter(Boolean);
        const scopeRows = Array.isArray(formData[`scope-${index}`]) ? formData[`scope-${index}`] : [];
        const standards = standardIds.map((stdId, idx) => ({
          standardId: stdId,
          scope: scopeRows[idx] !== undefined ? scopeRows[idx] : null
        }));

        return {
          serviceId: serviceId,
          standards: standards
        };
      })
    };
  };
  

  const handleFormSubmit = async (e, onSuccess, editingLead) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    let leadPayload;
    try {
      leadPayload = await createLeadPayload(formData, serviceSections);
    } catch (err) {
      setFormErrors((prev) => ({ ...prev, competitor: err?.response?.data?.message || 'Failed to create competitor' }));
      setIsSubmitting(false);
      return;
    }

    try {
      if (editingLead) {
        const response = await api.put(`/crm/leads/${editingLead.id}/json`, leadPayload);

        // Log update activity
        await ActivityLogger.logLeadActivity('UPDATE_LEAD', {
          id: editingLead.id,
          companyName: formData.companyName,
          ...leadPayload
        });

        // Send email notification when assigned user changes on edit
        const previousAssignedId = editingLead?.assignedTo?.id ?? editingLead?.assignedUserId ?? editingLead?.assignUser ?? null;
        if (leadPayload.assignedUserId && String(leadPayload.assignedUserId) !== String(previousAssignedId)) {
          (async () => {
            try {
              const [emailSettings, emailTemplates] = await Promise.all([
                checkEmailSettings(),
                getLeadEmailTemplates()
              ]);

              if (!emailSettings) return;

              let assignedUserEmail = null;
              try {
                const userResponse = await api.get(`/md/internal-users/${leadPayload.assignedUserId}`);
                assignedUserEmail = userResponse.data?.email;
              } catch (userError) {
                console.warn('⚠️ Failed to get assigned user email:', userError);
              }

              if (assignedUserEmail) {
                let companyName = '';
                let contactName = '';
                let stageName = '';
                let sectorName = '';
                let assignedUserName = '';

                try {
                  if (formData.company) {
                    const selectedCompany = options.companyOptions?.find(c => String(c.value) === String(formData.company));
                    if (selectedCompany) {
                      companyName = selectedCompany.label || selectedCompany.name || '';
                      sectorName = selectedCompany.sectorName || selectedCompany.sector || '';
                    }
                  }

                  if (formData.contact) {
                    const selectedContact = options.contactOptions?.find(c => String(c.value) === String(formData.contact));
                    if (selectedContact) {
                      contactName = selectedContact.label || selectedContact.name || '';
                    }
                  }

                  if (leadPayload.stage) {
                    const selectedStage = options.stageOptions?.find(s => String(s.value) === String(leadPayload.stage));
                    if (selectedStage) {
                      stageName = selectedStage.label || '';
                    }
                  }

                  const assignedUserResponse = await api.get(`/md/internal-users/${leadPayload.assignedUserId}`);
                  const userData = assignedUserResponse.data;
                  assignedUserName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'User';
                } catch (detailError) {
                  console.warn('⚠️ Failed to get additional lead details for email:', detailError);
                }

                const emailLeadData = {
                  id: editingLead.id,
                  projectName: leadPayload.projectName,
                  companyName,
                  contactName,
                  email: leadPayload.email,
                  stage: stageName,
                  sector: sectorName,
                  projectDescription: leadPayload.projectDescription,
                  assignedUser: assignedUserName,
                  createdBy: 'System',
                  createdAt: new Date().toLocaleDateString()
                };

                await sendLeadEmailToUser(assignedUserEmail, emailSettings, emailTemplates, emailLeadData);
                console.log('✅ Assignment email sent on lead update');
              }
            } catch (emailError) {
              console.error('❌ Failed to send assignment email on update:', emailError);
            }
          })();
        }
      } else {
        const response = await api.post('/crm/leads/json', leadPayload);

        // Log create activity
        await ActivityLogger.logLeadActivity('CREATE_LEAD', {
          id: response.data?.id,
          companyName: formData.companyName,
          ...leadPayload
        });

        // ✅ SEND EMAIL NOTIFICATION TO ASSIGNED USER (fire-and-forget to avoid blocking UI)
        if (leadPayload.assignedUserId) {
          (async () => {
            try {
              console.log('=== Sending email notification for new lead (async) ===');

              const [emailSettings, emailTemplates] = await Promise.all([
                checkEmailSettings(),
                getLeadEmailTemplates()
              ]);

              let assignedUserEmail = null;
              try {
                const userResponse = await api.get(`/md/internal-users/${leadPayload.assignedUserId}`);
                assignedUserEmail = userResponse.data?.email;
              } catch (userError) {
                console.warn('⚠️ Failed to get assigned user email:', userError);
              }

              if (emailSettings && assignedUserEmail) {
                // Get company, contact, and display names for email
                let companyName = '';
                let contactName = '';
                let stageName = '';
                let sectorName = '';
                let assignedUserName = '';

                try {
                  // Get company name and sector name from form options
                  if (formData.company) {
                    const selectedCompany = options.companyOptions?.find(c => String(c.value) === String(formData.company));
                    if (selectedCompany) {
                      companyName = selectedCompany.label || selectedCompany.name || '';
                      sectorName = selectedCompany.sectorName || selectedCompany.sector || '';
                    }
                  }

                  // Get contact name from form options
                  if (formData.contact) {
                    const selectedContact = options.contactOptions?.find(c => String(c.value) === String(formData.contact));
                    if (selectedContact) {
                      contactName = selectedContact.label || selectedContact.name || '';
                    }
                  }

                  // Get stage name from stage options
                  if (leadPayload.stage) {
                    const selectedStage = options.stageOptions?.find(s => String(s.value) === String(leadPayload.stage));
                    if (selectedStage) {
                      stageName = selectedStage.label || '';
                    }
                  }

                  // Get assigned user name
                  const assignedUserResponse = await api.get(`/md/internal-users/${leadPayload.assignedUserId}`);
                  const userData = assignedUserResponse.data;
                  assignedUserName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'User';
                } catch (detailError) {
                  console.warn('⚠️ Failed to get additional lead details:', detailError);
                }

                const emailLeadData = {
                  id: response.data?.id,
                  projectName: leadPayload.projectName,
                  companyName: companyName,
                  contactName: contactName,
                  email: leadPayload.email,
                  stage: stageName, // Use display name instead of ID
                  sector: sectorName, // Use display name instead of ID
                  projectDescription: leadPayload.projectDescription,
                  assignedUser: assignedUserName,
                  createdBy: 'System', // Could be enhanced to get actual user
                  createdAt: new Date().toLocaleDateString()
                };

                await sendLeadEmailToUser(assignedUserEmail, emailSettings, emailTemplates, emailLeadData);
                console.log('✅ Email notification sent successfully (async)');
              } else {
                console.warn('⚠️ Cannot send email - missing settings or user email');
                console.warn('Email Settings:', emailSettings ? 'Available' : 'Not configured');
                console.warn('Assigned To Email:', assignedUserEmail || 'Not found');
              }
            } catch (emailError) {
              console.error('❌ Failed to send email notification (async):', emailError);
            }
          })();
        }
      }
      onSuccess?.();
      resetForm();
    } catch (err) {
      throw err; // Let parent component handle the error
    } finally {
      setIsSubmitting(false);
    }
  };

  const addServiceSection = () => {
    setServiceSections(prev => [...prev, Math.max(...prev) + 1]);
  };

  const deleteServiceSection = (index) => {
    if (serviceSections.length > 1) {
      setServiceSections(prev => prev.filter(i => i !== index));
      setStandardsOptionsMap(prev => {
        const copy = { ...prev };
        delete copy[index];
        return copy;
      });
      
      // Clear form data for this section
      setFormData(prev => {
        const newData = { ...prev };
        delete newData[`services-${index}`];
        delete newData[`standards-${index}`];
        delete newData[`scope-${index}`];
        return newData;
      });
    }
  };

  // HAPUS: onDrop, removeFile functions

  const resetForm = () => {
    setFormData(initialFormData);
    setFormErrors({});
    setServiceSections([0]);
    // HAPUS: setUploadedFiles({...});
    setStandardsOptionsMap({});
  };

  const initializeServiceSections = async (services) => {
    if (!Array.isArray(services) || services.length === 0) {
      setServiceSections([0]);
      return;
    }

    // Create sections for each service
    const sectionIndices = Array.from({ length: services.length }, (_, i) => i);
    setServiceSections(sectionIndices);

    // Initialize form data for each service while preserving existing data
    setFormData(prevData => {
      const newData = { ...prevData }; // Preserve existing form data
      services.forEach((service, index) => {
        const serviceId = service.serviceId || '';
        newData[`services-${index}`] = serviceId;

        // standards is now [{standardId, scope}, ...] from the backend.
        // Extract IDs and per-row scopes in one pass.
        const standardsRaw = service.standards || [];
        const isPerRowShape = standardsRaw.length > 0 && standardsRaw[0] !== null && typeof standardsRaw[0] === 'object' && 'standardId' in standardsRaw[0];
        const standardIds = isPerRowShape
          ? standardsRaw.map(s => s.standardId).filter(Boolean)
          : (service.standardIds || standardsRaw).filter(Boolean);

        newData[`standards-${index}`] = standardIds;

        // Build per-row scope array
        let scopeRows;
        if (isPerRowShape) {
          const iscc = (service.scope && Array.isArray(service.scope));
          scopeRows = standardsRaw.map(s => {
            const raw = s.scope;
            if (!raw) return iscc ? [] : '';
            if (typeof raw === 'string' && raw.startsWith('[')) {
              try { return JSON.parse(raw); } catch (e) { return raw; }
            }
            return raw;
          });
          if (scopeRows.length === 0) scopeRows = [iscc ? [] : ''];
        } else {
          // Legacy: single scope on service object — put on first row
          let scopeValue = '';
          if (service.scope) {
            try {
              if (typeof service.scope === 'string' && service.scope.startsWith('[')) {
                const parsed = JSON.parse(service.scope);
                scopeValue = Array.isArray(parsed) ? parsed : [];
              } else {
                scopeValue = service.scope;
              }
            } catch (e) {
              scopeValue = service.scope || '';
            }
          }
          const rowCount = Math.max(standardIds.length, 1);
          scopeRows = Array.from({ length: rowCount }, (_, rowIdx) =>
            rowIdx !== 0 ? (Array.isArray(scopeValue) ? [] : '') : scopeValue
          );
        }

        newData[`scope-${index}`] = scopeRows;

      });
      return newData;
    });

    // Fetch all service standards in parallel (not inside setFormData to avoid serial await)
    const standardsFetches = services
      .map((service, index) => service.serviceId ? fetchStandardsByServiceId(index, service.serviceId) : null)
      .filter(Boolean);
    await Promise.all(standardsFetches);
  };

  const computedOptions = useMemo(() => ({
    ...options,
    assignedUserOptions: allAssignedUsers
  }), [options, allAssignedUsers]);

  return {
    formData,
    formErrors,
    serviceSections,
    // HAPUS: uploadedFiles,
    options: computedOptions,
    standardsOptionsMap,
    handleInputChange,
    handleTextChange,
    handleFormSubmit,
    addServiceSection,
    deleteServiceSection,
    // HAPUS: onDrop, removeFile,
    resetForm,
    fetchContactOptions,
    isEditMode,
    setIsEditMode,
    selectedLead,
    setSelectedLead,
    initializeServiceSections,
    isIsccService,
    isSubmitting,
    formCascadeUsers,
    formCascadeDepts
  };
};
